import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Error as ErrorIcon, CheckCircle, Pending, Warning, ArrowBack, Schedule } from '@mui/icons-material';
import { PickedItemVm } from 'api/picking/get-picked-items.vm';
import { getPickedItems } from 'api/picking/get-picked-items';
import { PickingVm, OrderPickingStatus } from 'api/picking/picking.vm';
import { getPicking } from 'api/picking/get-picking';

const PickingProcessPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pickingId } = useParams<{ pickingId: string }>();
  const [items, setItems] = useState<PickedItemVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [pickingDetails, setPickingDetails] = useState<PickingVm | null>(null);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const fetchPickedItems = (offset: string | null, reset: boolean = false) => {
    if (!pickingId) return;

    setLoading(true);

    getPickedItems(pickingId, offset || '', {
      success: (response) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setItems(reset ? response.objects : [...items, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter itens separados:', err);
        setLoading(false);
      },
    });
  };

  const fetchPickingDetails = async () => {
    if (!pickingId) return;

    setLoadingDetails(true);

    try {
      const response = await getPicking(pickingId);
      setPickingDetails(response);
      setLoadingDetails(false);
    } catch (err) {
      console.error('Falha ao obter detalhes do picking:', err);
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    fetchPickedItems(null, true);
    fetchPickingDetails();
  }, [pickingId]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchPickedItems(nextOffset);
  };

  const getStatusChip = (status: OrderPickingStatus) => {
    switch (status) {
      case OrderPickingStatus.IN_PROGRESS:
        return <Chip icon={<Schedule fontSize="small" />} label="Em Progresso" color="warning" size="small" />;
      case OrderPickingStatus.COMMITTED:
        return <Chip icon={<CheckCircle fontSize="small" />} label="Confirmado" color="success" size="small" />;
      case OrderPickingStatus.CANCELED:
        return <Chip icon={<ErrorIcon fontSize="small" />} label="Cancelado" color="error" size="small" />;
      case OrderPickingStatus.DOCUMENT_CREATION_FAIL:
        return <Chip icon={<ErrorIcon fontSize="small" />} label="Erro na Criação" color="error" size="small" />;
      case OrderPickingStatus.DOCUMENT_CREATION_SUCCESS:
        return <Chip icon={<CheckCircle fontSize="small" />} label="Documento Criado" color="success" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={handleBackNavigation}>
            Processos de Picking
          </Link>
          <Typography color="text.primary">Picking #{pickingId}</Typography>
        </Breadcrumbs>
      </Box>

      {loadingDetails ? (
        <CircularProgress />
      ) : pickingDetails ? (
        <Paper sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Detalhes do Picking
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                ID
              </Typography>
              <Typography variant="body1">{pickingDetails.id}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Pedido
              </Typography>
              <Typography variant="body1">{pickingDetails.orderId}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Estado
              </Typography>
              {getStatusChip(pickingDetails.status)}
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Data de Criação
              </Typography>
              <Typography variant="body1">{formatDate(pickingDetails.createdAt)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Separador
              </Typography>
              <Typography variant="body1">{pickingDetails.pickerName}</Typography>
            </Box>
          </Box>
          {pickingDetails.integrationError && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Erro de Integração
              </Typography>
              <Typography variant="body1" color="error">
                {pickingDetails.integrationError}
              </Typography>
            </Box>
          )}
        </Paper>
      ) : null}

      <Typography variant="h6" gutterBottom>
        Itens Separados
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Produto</TableCell>
              <TableCell>Linha de Encomenda</TableCell>
              <TableCell>Localização</TableCell>
              <TableCell>Lote</TableCell>
              <TableCell>Número de Série</TableCell>
              <TableCell>Separador</TableCell>
              <TableCell>Quantidade</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Erro</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.itemId}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {item.productName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      SKU: {item.productSku}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{item.orderItemId}</TableCell>
                <TableCell>{item.locationId}</TableCell>
                <TableCell>{item.batch || '-'}</TableCell>
                <TableCell>{item.serialNumber || '-'}</TableCell>
                <TableCell>{item.pickerName}</TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>
                  <Chip label="Separado" color="success" size="small" />
                </TableCell>
                <TableCell>{item.integrationError || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {hasMore && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Button variant="outlined" onClick={handleLoadMore}>
            Carregar Mais
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PickingProcessPage;
